import {useState, useEffect, KeyboardEvent} from 'react';

interface UseDropdownKeyboardNavigationProps<T> {
  items: T[];
  onSelect: (item: T) => void;
  isOpen: boolean;
  itemDisabled?: (item: T) => boolean;
  enableKeyboardNavigation?: boolean;
  initialIndex?: number; // new prop to start highlight on selected item
}

export function useDropdownKeyboardNavigation<T>({
  items,
  onSelect,
  isOpen,
  itemDisabled,
  enableKeyboardNavigation = true,
  initialIndex = 0,
}: UseDropdownKeyboardNavigationProps<T>) {
  const [highlightedIndex, setHighlightedIndex] =
    useState<number>(initialIndex);
  const [typeBuffer, setTypeBuffer] = useState('');
  const [bufferTimer, setBufferTimer] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);

  // Reset highlight when items or open state change
  useEffect(() => {
    if (!enableKeyboardNavigation) return;

    if (!isOpen) {
      setHighlightedIndex(-1);
    }
    setHighlightedIndex((prev) =>
      prev === -1 && items?.length > 0
        ? initialIndex >= 0
          ? initialIndex
          : 0
        : prev
    );
  }, [isOpen]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!enableKeyboardNavigation) return;

    const {key} = event;

    if (!isOpen) return;

    if (key.length === 1 && /^[a-z0-9]$/i.test(key)) {
      const newBuffer = (typeBuffer + key).toLowerCase();
      setTypeBuffer(newBuffer);

      if (bufferTimer) clearTimeout(bufferTimer);

      const timer = setTimeout(() => {
        setTypeBuffer('');
      }, 500);

      setBufferTimer(timer);

      const getItemText = (item: any): string => {
        if (typeof item?.name === 'string') return item.name;
        if (typeof item?.label === 'string') return item.label;
        if (typeof item?.value === 'string') return item.value;
        if (item?.id !== undefined && item?.id !== null) return String(item.id);

        return '';
      };

      const searchText = newBuffer;

      // Start searching AFTER current highlight
      let matchIndex = -1;

      //Search forward from next index
      for (let i = highlightedIndex + 1; i < items.length; i++) {
        const text = getItemText(items[i]).toLowerCase();
        if (text.startsWith(searchText)) {
          matchIndex = i;
          break;
        }
      }

      //If not found, wrap to beginning
      if (matchIndex === -1) {
        for (let i = 0; i <= highlightedIndex; i++) {
          const text = getItemText(items[i]).toLowerCase();
          if (text.startsWith(searchText)) {
            matchIndex = i;
            break;
          }
        }
      }

      if (matchIndex >= 0) {
        setHighlightedIndex(matchIndex); //highlight only
      }

      return; //prevent other handling
    }

    if (key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((prev) => {
        let next = prev + 1;
        while (next < items.length && itemDisabled?.(items[next])) next++;
        return next >= items.length ? 0 : next;
      });
    } else if (key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((prev) => {
        let next = prev - 1;
        while (next >= 0 && itemDisabled?.(items[next])) next--;
        return next < 0 ? items.length - 1 : next;
      });
    } else if (key === 'Enter') {
      event.preventDefault();
      if (highlightedIndex >= 0 && items[highlightedIndex]) {
        onSelect(items[highlightedIndex]);
      }
    } else if (key === 'Escape') {
      setHighlightedIndex(-1);
    }
  };

  return {
    highlightedIndex,
    handleKeyDown,
    setHighlightedIndex,
  };
}
